import React, { useEffect, useMemo, useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase.js';
import { useAuth } from '../../context/AuthContext.js';
import { ALL_PERMISSIONS } from '../../constants/permissions.js';
import { PERMISSION_GROUPS, PERMISSION_INFO, ROLE_DESCRIPTIONS, ROLE_PERMISSION_PRESETS, PANEL_PERMISSION_DEPENDENCIES, FUNCTION_PERMISSION_DEPENDENTS } from '../../constants/permissionCatalog.js';
import '../../styles/PermissionsConfig.css';

const ROLE_SEQUENCE = ['admin', 'support'];
const TAB_OPTIONS = [
    { id: 'permissions', label: 'Permisos' },
    { id: 'wallets', label: 'Billeteras' },
    { id: 'global', label: 'Pagos globales' },
];

const PANEL_DEPENDENCIES = PANEL_PERMISSION_DEPENDENCIES;
const FUNCTION_DEPENDENTS = FUNCTION_PERMISSION_DEPENDENTS;
const PANEL_PERMISSION_SET = new Set(Object.keys(PANEL_DEPENDENCIES));

const COMMON_WALLET_LABELS = [
    'USDT (TRC20)',
    'USDT (BEP20)',
    'USDT (ERC20)',
    'USDC (TRC20)',
    'USDC (ERC20)',
    'Bitcoin (BTC)',
    'Ethereum (ETH)',
    'BNB Smart Chain (BEP20)',
    'BUSD (BEP20)',
    'Tron (TRX)',
];

const DEFAULT_GLOBAL_DECIMALS = {
    dailyGainRateNormal: 0.008,
    dailyGainRateAccelerator: 0.012,
    globalPaymentCapMultiplier: 2,
};

const normalizePreset = (preset = {}) => {
    const normalized = {};
    ALL_PERMISSIONS.forEach(permission => {
        normalized[permission] = !!preset[permission];
    });
    return normalized;
};

const mergePresets = (platformConfig) => {
    const storedPresets = platformConfig?.rolePermissionPresets || {};
    return ROLE_SEQUENCE.reduce((acc, role) => {
        const basePreset = ROLE_PERMISSION_PRESETS[role] || {};
        const storedPreset = storedPresets[role] || {};
        acc[role] = normalizePreset({ ...basePreset, ...storedPreset });
        return acc;
    }, {});
};

const PermissionsConfig = () => {
    const { platformConfig, setPlatformConfig } = useAuth();
    const presetsFromConfig = useMemo(() => mergePresets(platformConfig), [platformConfig]);
    const walletsFromConfig = useMemo(() => {
        const raw = platformConfig?.depositWallets;
        if (Array.isArray(raw)) {
            return raw.map((wallet, index) => ({
                id: wallet?.id || `wallet-${index}-${wallet?.label || 'item'}`,
                label: wallet?.label || '',
                address: wallet?.address || '',
            }));
        }
        if (raw && typeof raw === 'object') {
            return Object.entries(raw).map(([label, address], index) => ({
                id: `wallet-${index}-${label || 'item'}`,
                label: label || '',
                address: address || '',
            }));
        }
        return [];
    }, [platformConfig]);
    const globalFromConfig = useMemo(() => ({
        normalRate: ((platformConfig?.dailyGainRateNormal ?? DEFAULT_GLOBAL_DECIMALS.dailyGainRateNormal) * 100).toFixed(2),
        acceleratorRate: ((platformConfig?.dailyGainRateAccelerator ?? DEFAULT_GLOBAL_DECIMALS.dailyGainRateAccelerator) * 100).toFixed(2),
        capMultiplier: String(platformConfig?.globalPaymentCapMultiplier ?? DEFAULT_GLOBAL_DECIMALS.globalPaymentCapMultiplier),
    }), [platformConfig]);

    const [activeTab, setActiveTab] = useState('permissions');
    const [draftPresets, setDraftPresets] = useState(() => presetsFromConfig);
    const [walletDraft, setWalletDraft] = useState(() => walletsFromConfig);
    const [globalDraft, setGlobalDraft] = useState(() => globalFromConfig);
    const [activeRole, setActiveRole] = useState('admin');
    const [savingTab, setSavingTab] = useState(null);
    const [feedback, setFeedback] = useState(null);

    useEffect(() => {
        setDraftPresets(presetsFromConfig);
    }, [presetsFromConfig]);

    useEffect(() => {
        setWalletDraft(walletsFromConfig);
    }, [walletsFromConfig]);

    useEffect(() => {
        setGlobalDraft(globalFromConfig);
    }, [globalFromConfig]);

    const hasPermissionChanges = useMemo(() =>
        JSON.stringify(draftPresets) !== JSON.stringify(presetsFromConfig)
    , [draftPresets, presetsFromConfig]);

    const simplifyWallets = (wallets) => wallets.map(({ label = '', address = '' }) => ({
        label: label.trim(),
        address: address.trim(),
    }));

    const walletsDirty = useMemo(() =>
        JSON.stringify(simplifyWallets(walletDraft)) !== JSON.stringify(simplifyWallets(walletsFromConfig))
    , [walletDraft, walletsFromConfig]);

    const globalDirty = useMemo(() =>
        JSON.stringify(globalDraft) !== JSON.stringify(globalFromConfig)
    , [globalDraft, globalFromConfig]);

    const handleToggle = (role, permission) => {
        setDraftPresets(prev => {
            const previousRolePermissions = prev[role] || {};
            const nextRolePermissions = { ...previousRolePermissions };
            const nextValue = !nextRolePermissions[permission];
            nextRolePermissions[permission] = nextValue;

            if (nextValue && PANEL_DEPENDENCIES[permission]) {
                PANEL_DEPENDENCIES[permission].forEach((requiredPermission) => {
                    nextRolePermissions[requiredPermission] = true;
                });
            }

            if (!nextValue && FUNCTION_DEPENDENTS[permission]) {
                FUNCTION_DEPENDENTS[permission].forEach((panelPermission) => {
                    nextRolePermissions[panelPermission] = false;
                });
            }

            return {
                ...prev,
                [role]: nextRolePermissions,
            };
        });
    };

    const handleSelectRole = (role) => {
        setActiveRole(role);
    };

    const handleResetDefaults = () => {
        if (!window.confirm('¿Restaurar los permisos a sus valores predeterminados?')) {
            return;
        }
        const defaults = mergePresets(null);
        setDraftPresets(defaults);
        setFeedback({ tab: 'permissions', type: 'info', message: 'Valores predeterminados restaurados. Guarda para aplicarlos.' });
    };

    const handleSavePermissions = async () => {
        setSavingTab('permissions');
        setFeedback(null);
        const payload = { rolePermissionPresets: draftPresets };
        try {
            await setDoc(doc(db, 'platform_config', 'main'), payload, { merge: true });
            setPlatformConfig(prev => (prev ? { ...prev, ...payload } : { ...payload }));
            setFeedback({ tab: 'permissions', type: 'success', message: 'Permisos actualizados correctamente.' });
        } catch (error) {
            console.error('Error al guardar permisos:', error);
            setFeedback({ tab: 'permissions', type: 'error', message: 'No se pudieron guardar los cambios. Inténtalo nuevamente.' });
        } finally {
            setSavingTab(null);
        }
    };

    const handleWalletChange = (id, field, value) => {
        setWalletDraft(prev => prev.map(wallet =>
            wallet.id === id ? { ...wallet, [field]: value } : wallet
        ));
    };

    const handleAddWallet = () => {
        const uniqueId = `wallet-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
        setWalletDraft(prev => ([
            ...prev,
            { id: uniqueId, label: '', address: '' },
        ]));
        setFeedback(null);
    };

    const handleRemoveWallet = (id) => {
        setWalletDraft(prev => prev.filter(wallet => wallet.id !== id));
        setFeedback(null);
    };

    const handleResetWallets = () => {
        setWalletDraft(walletsFromConfig);
        setFeedback({ tab: 'wallets', type: 'info', message: 'Valores restaurados a la configuración guardada.' });
    };

    const handleSaveWallets = async () => {
        setSavingTab('wallets');
        setFeedback(null);
        const sanitized = walletDraft
            .map(({ label, address }) => ({ label: (label || '').trim(), address: (address || '').trim() }))
            .filter(item => item.label || item.address);

        try {
            const payload = { depositWallets: sanitized };
            await setDoc(doc(db, 'platform_config', 'main'), payload, { merge: true });
            setPlatformConfig(prev => (prev ? { ...prev, ...payload } : { ...payload }));
            setWalletDraft(sanitized.map((wallet, index) => ({
                id: `wallet-${index}-${wallet.label || 'item'}`,
                ...wallet,
            })));
            setFeedback({ tab: 'wallets', type: 'success', message: 'Billeteras actualizadas correctamente.' });
        } catch (error) {
            console.error('Error al guardar billeteras:', error);
            setFeedback({ tab: 'wallets', type: 'error', message: 'No se pudieron guardar las billeteras. Inténtalo nuevamente.' });
        } finally {
            setSavingTab(null);
        }
    };

    const handleGlobalChange = (field, value) => {
        setGlobalDraft(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleResetGlobal = () => {
        setGlobalDraft(globalFromConfig);
        setFeedback({ tab: 'global', type: 'info', message: 'Valores restaurados a la configuración guardada.' });
    };

    const handleSaveGlobal = async () => {
        setSavingTab('global');
        setFeedback(null);

        const parsedNormal = Number(globalDraft.normalRate);
        const parsedAccelerator = Number(globalDraft.acceleratorRate);
        const parsedCap = Number(globalDraft.capMultiplier);

        if ([parsedNormal, parsedAccelerator, parsedCap].some(value => Number.isNaN(value))) {
            setFeedback({ tab: 'global', type: 'error', message: 'Completa todos los campos con valores numéricos válidos.' });
            setSavingTab(null);
            return;
        }

        if (parsedNormal <= 0 || parsedAccelerator <= 0) {
            setFeedback({ tab: 'global', type: 'error', message: 'Las tasas deben ser mayores a 0.' });
            setSavingTab(null);
            return;
        }

        if (parsedCap < 1) {
            setFeedback({ tab: 'global', type: 'error', message: 'El multiplicador debe ser al menos 1.' });
            setSavingTab(null);
            return;
        }

        const payload = {
            dailyGainRateNormal: parsedNormal / 100,
            dailyGainRateAccelerator: parsedAccelerator / 100,
            globalPaymentCapMultiplier: parsedCap,
        };

        try {
            await setDoc(doc(db, 'platform_config', 'main'), payload, { merge: true });
            setPlatformConfig(prev => (prev ? { ...prev, ...payload } : { ...payload }));
            setGlobalDraft({
                normalRate: parsedNormal.toFixed(2),
                acceleratorRate: parsedAccelerator.toFixed(2),
                capMultiplier: String(parsedCap),
            });
            setFeedback({ tab: 'global', type: 'success', message: 'Parámetros de pagos globales actualizados correctamente.' });
        } catch (error) {
            console.error('Error al guardar pagos globales:', error);
            setFeedback({ tab: 'global', type: 'error', message: 'No se pudieron guardar los cambios. Inténtalo nuevamente.' });
        } finally {
            setSavingTab(null);
        }
    };

    const activePreset = draftPresets[activeRole];
    const roleDescription = ROLE_DESCRIPTIONS[activeRole];
    const activeCount = useMemo(() => {
        if (!activePreset) return 0;
        return Object.values(activePreset).filter(Boolean).length;
    }, [activePreset]);

    const activePanels = useMemo(() => {
        if (!activePreset) return [];
        return Object.entries(activePreset)
            .filter(([permission, enabled]) => enabled && PANEL_PERMISSION_SET.has(permission))
            .map(([permission]) => permission);
    }, [activePreset]);

    const activePanelSummaries = useMemo(() => {
        if (!activePanels.length) {
            return [];
        }
        return activePanels
            .map(permission => PERMISSION_INFO[permission]?.label || permission)
            .sort();
    }, [activePanels]);

    const tabSubtitles = {
        permissions: 'Define qué acciones puede realizar cada rol del panel administrativo. Los cambios se aplican en tiempo real a los nuevos ajustes de rol.',
        wallets: 'Administra las billeteras oficiales que se muestran en los formularios de aporte y retiro.',
        global: 'Ajusta los parámetros usados para calcular los pagos globales diarios en la plataforma.',
    };

    const currentSubtitle = tabSubtitles[activeTab] || tabSubtitles.permissions;
    const currentFeedback = feedback && feedback.tab === activeTab ? feedback : null;
    const isSavingCurrent = savingTab === activeTab;

    const renderFooter = () => {
        switch (activeTab) {
            case 'permissions':
                return (
                    <footer className="permissions-config__actions">
                        {currentFeedback && (
                            <span className={`feedback-message feedback-message--${currentFeedback.type}`}>
                                {currentFeedback.message}
                            </span>
                        )}
                        <div className="permissions-config__buttons">
                            <button
                                type="button"
                                className="action-button ghost"
                                onClick={handleResetDefaults}
                                disabled={isSavingCurrent}
                            >
                                Restaurar predeterminados
                            </button>
                            <button
                                type="button"
                                className="login-button"
                                onClick={handleSavePermissions}
                                disabled={isSavingCurrent || !hasPermissionChanges}
                            >
                                {isSavingCurrent ? 'Guardando...' : 'Guardar cambios'}
                            </button>
                        </div>
                    </footer>
                );
            case 'wallets':
                return (
                    <footer className="permissions-config__actions">
                        {currentFeedback && (
                            <span className={`feedback-message feedback-message--${currentFeedback.type}`}>
                                {currentFeedback.message}
                            </span>
                        )}
                        <div className="permissions-config__buttons">
                            <button
                                type="button"
                                className="action-button ghost"
                                onClick={handleResetWallets}
                                disabled={isSavingCurrent}
                            >
                                Restaurar valores guardados
                            </button>
                            <button
                                type="button"
                                className="login-button"
                                onClick={handleSaveWallets}
                                disabled={isSavingCurrent || !walletsDirty}
                            >
                                {isSavingCurrent ? 'Guardando...' : 'Guardar billeteras'}
                            </button>
                        </div>
                    </footer>
                );
            case 'global':
                return (
                    <footer className="permissions-config__actions">
                        {currentFeedback && (
                            <span className={`feedback-message feedback-message--${currentFeedback.type}`}>
                                {currentFeedback.message}
                            </span>
                        )}
                        <div className="permissions-config__buttons">
                            <button
                                type="button"
                                className="action-button ghost"
                                onClick={handleResetGlobal}
                                disabled={isSavingCurrent}
                            >
                                Restaurar valores guardados
                            </button>
                            <button
                                type="button"
                                className="login-button"
                                onClick={handleSaveGlobal}
                                disabled={isSavingCurrent || !globalDirty}
                            >
                                {isSavingCurrent ? 'Guardando...' : 'Guardar parámetros'}
                            </button>
                        </div>
                    </footer>
                );
            default:
                return null;
        }
    };

    return (
        <div className="permissions-config">
            <header className="permissions-config__header">
                <h2>Configuración del panel administrativo</h2>
                <p>{currentSubtitle}</p>
            </header>

            <div className="config-tabs">
                {TAB_OPTIONS.map(tab => (
                    <button
                        key={tab.id}
                        type="button"
                        className={`tab-button ${activeTab === tab.id ? 'tab-button--active' : ''}`}
                        aria-pressed={activeTab === tab.id}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'permissions' && (
                <>
                    <p className="config-hint">
                        Los participantes nuevos conservan automáticamente el rol <strong>user</strong> con su panel estándar y no se puede modificar desde esta pantalla.
                        Solo los roles <strong>Administrador</strong> y <strong>Soporte</strong> pueden personalizarse y asignarse manualmente desde el panel de super admin.
                    </p>
                    <div className="role-switcher">
                        {ROLE_SEQUENCE.map(role => {
                            const roleInfo = ROLE_DESCRIPTIONS[role];
                            const totalEnabled = Object.values(draftPresets[role] || {}).filter(Boolean).length;
                            const isActive = role === activeRole;
                            return (
                                <button
                                    key={role}
                                    type="button"
                                    className={`role-pill ${isActive ? 'role-pill--active' : ''}`}
                                    onClick={() => handleSelectRole(role)}
                                >
                                    <span className="role-pill__label">{roleInfo?.label || role}</span>
                                    <span className="role-pill__count">{totalEnabled} permisos activos</span>
                                </button>
                            );
                        })}
                    </div>

                    <section className="role-summary">
                        <h3>{roleDescription?.label || 'Rol seleccionado'}</h3>
                        <p>{roleDescription?.summary}</p>
                        <div className="role-summary__metrics">
                            <span className="role-summary__badge">{activeCount} de {ALL_PERMISSIONS.length} permisos habilitados</span>
                            <span className="role-summary__badge role-summary__badge--secondary">{activePanels.length} módulos visibles</span>
                        </div>
                        <div className="role-summary__modules">
                            {activePanelSummaries.length ? (
                                activePanelSummaries.map(label => (
                                    <span key={label} className="role-summary__module-chip">{label}</span>
                                ))
                            ) : (
                                <span className="role-summary__empty">Este rol no tiene módulos del panel activos.</span>
                            )}
                        </div>
                    </section>

                    <div className="permission-groups">
                        {PERMISSION_GROUPS.map(group => (
                            <div key={group.key} className="permission-group-card">
                                <header className="permission-group-card__header">
                                    <h4>{group.title}</h4>
                                    <span className="permission-group-card__meta">
                                        {group.permissions.filter(permission => activePreset?.[permission]).length} / {group.permissions.length}
                                    </span>
                                </header>
                                <div className="permission-group-card__body">
                                    {group.permissions.map(permission => {
                                        const info = PERMISSION_INFO[permission] || { label: permission, description: '' };
                                        const isEnabled = activePreset?.[permission] || false;
                                        const required = PANEL_DEPENDENCIES[permission] || [];
                                        const dependents = FUNCTION_DEPENDENTS[permission] || [];
                                        return (
                                            <div key={permission} className={`permission-item ${isEnabled ? 'permission-item--active' : ''}`}>
                                                <div className="permission-item__text">
                                                    <span className="permission-item__label">{info.label}</span>
                                                    {info.description && (
                                                        <small className="permission-item__description">{info.description}</small>
                                                    )}
                                                    {(required.length > 0 || dependents.length > 0) && (
                                                        <div className="permission-item__chips">
                                                            {required.map((dependency) => (
                                                                <span key={`${permission}-needs-${dependency}`} className="permission-chip permission-chip--requires">Requiere: {PERMISSION_INFO[dependency]?.label || dependency}</span>
                                                            ))}
                                                            {dependents.map((panelPermission) => (
                                                                <span key={`${permission}-enables-${panelPermission}`} className="permission-chip permission-chip--enables">Habilita: {PERMISSION_INFO[panelPermission]?.label || panelPermission}</span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <label className="permission-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={isEnabled}
                                                        onChange={() => handleToggle(activeRole, permission)}
                                                    />
                                                    <span className="permission-toggle__slider" />
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {activeTab === 'wallets' && (
                <section className="config-section-card">
                    <h3>Billeteras oficiales</h3>
                    <p className="config-hint">Estas direcciones se muestran a los usuarios cuando realizan aportes o retiros. Asegúrate de que estén verificadas antes de guardarlas.</p>
                    <datalist id="wallet-label-options">
                        {COMMON_WALLET_LABELS.map(option => (
                            <option key={option} value={option} />
                        ))}
                    </datalist>
                    <small className="config-hint">Selecciona un nombre frecuente desde la lista o escribe uno personalizado.</small>
                    <div className="config-grid config-grid--two">
                        {walletDraft.length === 0 && (
                            <p className="config-hint">Aún no hay billeteras configuradas. Agrega al menos una dirección cuando sea necesario.</p>
                        )}
                        {walletDraft.map((wallet, index) => (
                            <div key={wallet.id} className="wallet-row">
                                <div className="config-field">
                                    <label htmlFor={`wallet-label-${wallet.id}`}>Nombre visible</label>
                                    <input
                                        id={`wallet-label-${wallet.id}`}
                                        type="text"
                                        list="wallet-label-options"
                                        placeholder={`Ej. USDT (BEP20) #${index + 1}`}
                                        value={wallet.label}
                                        onChange={(e) => handleWalletChange(wallet.id, 'label', e.target.value)}
                                    />
                                </div>
                                <div className="config-field">
                                    <label htmlFor={`wallet-address-${wallet.id}`}>Dirección</label>
                                    <input
                                        id={`wallet-address-${wallet.id}`}
                                        type="text"
                                        placeholder="Ej. 0x1100..."
                                        value={wallet.address}
                                        onChange={(e) => handleWalletChange(wallet.id, 'address', e.target.value)}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="wallet-remove"
                                    onClick={() => handleRemoveWallet(wallet.id)}
                                    aria-label={`Eliminar billetera ${wallet.label || index + 1}`}
                                >
                                    Eliminar
                                </button>
                            </div>
                        ))}
                    </div>
                    <button type="button" className="add-wallet-button" onClick={handleAddWallet}>
                        + Agregar billetera
                    </button>
                    <small className="config-hint">Los cambios se guardan en el documento <code>platform_config/main</code> y se reflejan inmediatamente en la app.</small>
                </section>
            )}

            {activeTab === 'global' && (
                <section className="config-section-card">
                    <h3>Parámetros de pagos globales</h3>
                    <p className="config-hint">Estos valores son utilizados por las funciones automáticas para calcular el rendimiento diario de los acuerdos.</p>
                    <div className="config-grid config-grid--three">
                        <div className="config-field">
                            <label htmlFor="global-normal">ROI diario estándar (%)</label>
                            <input
                                id="global-normal"
                                type="number"
                                step="0.01"
                                min="0"
                                value={globalDraft.normalRate}
                                onChange={(e) => handleGlobalChange('normalRate', e.target.value)}
                            />
                        </div>
                        <div className="config-field">
                            <label htmlFor="global-accelerator">ROI diario con acelerador (%)</label>
                            <input
                                id="global-accelerator"
                                type="number"
                                step="0.01"
                                min="0"
                                value={globalDraft.acceleratorRate}
                                onChange={(e) => handleGlobalChange('acceleratorRate', e.target.value)}
                            />
                        </div>
                        <div className="config-field">
                            <label htmlFor="global-cap">Multiplicador de tope</label>
                            <input
                                id="global-cap"
                                type="number"
                                step="0.1"
                                min="1"
                                value={globalDraft.capMultiplier}
                                onChange={(e) => handleGlobalChange('capMultiplier', e.target.value)}
                            />
                        </div>
                    </div>
                    <small className="config-hint">Ejemplo: un ROI del 0.80% equivale a guardar <strong>0.008</strong> en Firestore.</small>
                </section>
            )}

            {renderFooter()}
        </div>
    );
};

export default PermissionsConfig;
